import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, apiDownload } from "../lib/api";
import type { AuditEntry, Page } from "../lib/types";
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  PageHeader,
  Select,
  Table,
  TableWrap,
  Td,
  Th,
  formatDate,
} from "../components/ui";

/**
 * The headings actions are grouped under. Filtering by one of these answers
 * "what happened to the money" or "what happened to this licence" without
 * anybody having to remember the exact name of every action.
 */
const HEADINGS = [
  { value: "", label: "Everything" },
  { value: "license.", label: "Licences" },
  { value: "order.", label: "Orders" },
  { value: "subscription.", label: "Subscriptions" },
  { value: "commission.", label: "Commission" },
  { value: "auth.", label: "Sign-in" },
  { value: "mail.", label: "Email" },
  { value: "checkout.", label: "Checkout" },
  { value: "signing_key.", label: "Signing keys" },
];

const ACTORS = [
  { value: "", label: "Anyone" },
  { value: "admin", label: "Staff" },
  { value: "customer", label: "Customers" },
  { value: "marketer", label: "Marketers" },
  { value: "stripe", label: "Stripe" },
  { value: "system", label: "The system" },
  { value: "api", label: "Licensed software" },
];

export function AuditLog() {
  const [heading, setHeading] = useState("");
  const [action, setAction] = useState("");
  const [actorType, setActorType] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);

  /** Everything except paging — the export covers the filter, not the page. */
  const filters = new URLSearchParams();
  if (action.trim().length > 0) filters.set("action", action.trim());
  else if (heading.length > 0) filters.set("actionPrefix", heading);
  if (actorType.length > 0) filters.set("actorType", actorType);
  // A date alone means midnight; the end of a range has to mean the end of
  // that day or picking one day returns nothing.
  if (since.length > 0) filters.set("since", `${since}T00:00:00.000Z`);
  if (until.length > 0) filters.set("until", `${until}T23:59:59.999Z`);

  const params = new URLSearchParams(filters);
  if (cursor !== null) params.set("cursor", cursor);

  const entries = useQuery({
    queryKey: ["audit", params.toString()],
    queryFn: () => api<Page<AuditEntry>>(`/api/v1/admin/audit?${params.toString()}`),
  });

  const exportCsv = useMutation({
    mutationFn: () =>
      apiDownload(`/api/v1/admin/audit.csv?${filters.toString()}`, "servicefinder-audit.csv"),
  });

  /** Changing what is shown has to send paging back to the top. */
  const refilter = (apply: () => void) => {
    apply();
    setCursor(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit"
        lede="Every change, written in the same transaction as the change itself. This is the record when a customer disputes being shut off."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Field label="Show">
          <Select
            value={heading}
            onChange={(event) => refilter(() => setHeading(event.target.value))}
          >
            {HEADINGS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="By">
          <Select
            value={actorType}
            onChange={(event) => refilter(() => setActorType(event.target.value))}
          >
            {ACTORS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="From">
          <Input
            type="date"
            value={since}
            onChange={(event) => refilter(() => setSince(event.target.value))}
          />
        </Field>

        <Field label="To">
          <Input
            type="date"
            value={until}
            onChange={(event) => refilter(() => setUntil(event.target.value))}
          />
        </Field>

        <Field label="Exact action" hint="Overrides Show">
          <Input
            value={action}
            className="tabular"
            placeholder="license.revoked"
            onChange={(event) => refilter(() => setAction(event.target.value))}
          />
        </Field>
      </div>

      <Card>
        {entries.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : (entries.data?.items.length ?? 0) === 0 ? (
          <EmptyState>Nothing recorded for that filter.</EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>When</Th>
                  <Th>Action</Th>
                  <Th>Actor</Th>
                  <Th>Licence</Th>
                  <Th>IP</Th>
                </tr>
              </thead>
              <tbody>
                {(entries.data?.items ?? []).map((entry) => (
                  <tr key={entry.id} className="border-b border-hairline last:border-0">
                    <Td label="When" className="whitespace-nowrap text-muted">
                      {formatDate(entry.createdAt)}
                    </Td>
                    <Td label="Action" className="tabular">
                      {entry.action}
                    </Td>
                    <Td label="Actor" className="text-muted">
                      {entry.actorType}
                    </Td>
                    <Td label="Licence">
                      {entry.licenseId === null ? (
                        <span className="text-dim">—</span>
                      ) : (
                        <Link
                          to={`/licenses/${entry.licenseId}`}
                          className="tabular text-accent underline-offset-4 hover:underline"
                        >
                          open
                        </Link>
                      )}
                    </Td>
                    <Td label="IP" className="tabular text-dim">
                      {entry.ipAddress ?? "—"}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {exportCsv.isError && <ErrorNote>{(exportCsv.error as Error).message}</ErrorNote>}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <Button onClick={() => setCursor(null)} disabled={cursor === null}>
            Most recent
          </Button>
          <Button
            onClick={() => setCursor(entries.data?.nextCursor ?? null)}
            disabled={entries.data?.nextCursor == null}
          >
            Older
          </Button>
        </div>

        <Button disabled={exportCsv.isPending} onClick={() => exportCsv.mutate()}>
          {exportCsv.isPending ? "Preparing…" : "Export CSV"}
        </Button>
      </div>

      <p className="text-xs text-dim">
        The export covers everything matching these filters, oldest first — not just the page shown
        — up to 10,000 rows. Narrow the dates if you need more than that.
      </p>
    </div>
  );
}
